import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Group,
  Loader,
  Pagination,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
  APIBreakdown,
  getBreakdown,
  listVendors,
} from '../../api/tracking';
import { PAGE_SIZE, STATUS_CODE_OPTIONS } from './constants';
import {
  dateToRFC3339End,
  dateToRFC3339Start,
  getDefaultDateRange,
} from './dateUtils';

const BreakdownView = () => {
  const defaults = getDefaultDateRange();

  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);
  const [vendor, setVendor] = useState<string | null>(null);
  const [apiName, setApiName] = useState<string | null>(null);
  const [statusCode, setStatusCode] = useState<string | null>(null);

  const [vendors, setVendors] = useState<string[]>([]);
  const [apiNameOptions, setApiNameOptions] = useState<string[]>([]);
  const [items, setItems] = useState<APIBreakdown[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [page, setPage] = useState(1);

  const vendorSelected = Boolean(vendor);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, page]);

  const loadVendors = useCallback(async () => {
    setLoadingVendors(true);
    try {
      const list = await listVendors();
      setVendors(list);
    } catch {
      // axios interceptor already notifies
    } finally {
      setLoadingVendors(false);
    }
  }, []);

  const fetchBreakdown = useCallback(async () => {
    if (!fromDate || !toDate) {
      showNotification({
        message: 'Date range (from and to) is required',
        color: 'red',
      });
      return;
    }
    if (fromDate > toDate) {
      showNotification({
        message: 'From date must be on or before to date',
        color: 'red',
      });
      return;
    }

    setLoading(true);
    try {
      const data = await getBreakdown({
        from: dateToRFC3339Start(fromDate),
        to: dateToRFC3339End(toDate),
        ...(vendor ? { vendor } : {}),
        ...(vendor && apiName ? { api_name: apiName } : {}),
        ...(vendor && statusCode ? { status_code: statusCode } : {}),
      });
      setItems(data);
      setPage(1);

      if (vendor) {
        const names = Array.from(
          new Set(data.map((row) => row.api_name).filter(Boolean)),
        ).sort();
        setApiNameOptions((prev) => {
          if (apiName && !names.includes(apiName)) {
            return Array.from(new Set([...names, apiName])).sort();
          }
          return names.length > 0 ? names : prev;
        });
      }
    } catch {
      // axios interceptor already notifies
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, vendor, apiName, statusCode]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  // Initial load with default last-2-days range
  useEffect(() => {
    fetchBreakdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When vendor is selected, refresh api-name options for that vendor (date range only)
  useEffect(() => {
    if (!vendor || !fromDate || !toDate || fromDate > toDate) {
      setApiNameOptions([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await getBreakdown({
          from: dateToRFC3339Start(fromDate),
          to: dateToRFC3339End(toDate),
          vendor,
        });
        if (cancelled) return;
        const names = Array.from(
          new Set(data.map((row) => row.api_name).filter(Boolean)),
        ).sort();
        setApiNameOptions(names);
      } catch {
        // ignore — interceptor notifies
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [vendor, fromDate, toDate]);

  const handleVendorChange = (value: string | null) => {
    setVendor(value);
    setApiName(null);
    setStatusCode(null);
    if (!value) {
      setApiNameOptions([]);
    }
  };

  const totals = useMemo(() => {
    return items.reduce(
      (acc, row) => {
        acc.total += row.total;
        acc.success += row.success;
        acc.failure += row.failure;
        return acc;
      },
      { total: 0, success: 0, failure: 0 },
    );
  }, [items]);

  return (
    <Stack gap="lg">
      <Paper
        p="md"
        radius="sm"
        withBorder
        style={{ borderColor: '#d0d7de', background: '#ffffff' }}
      >
        <Stack gap="md">
          <Text fw={600} size="sm" c="#1f2328">
            Date range
          </Text>
          <Group gap="md" align="flex-end" grow preventGrowOverflow={false}>
            <TextInput
              label="From"
              type="date"
              required
              value={fromDate}
              onChange={(e) => setFromDate(e.currentTarget.value)}
              styles={{ input: { borderColor: '#d0d7de' } }}
            />
            <TextInput
              label="To"
              type="date"
              required
              value={toDate}
              onChange={(e) => setToDate(e.currentTarget.value)}
              styles={{ input: { borderColor: '#d0d7de' } }}
            />
          </Group>

          <Text fw={600} size="sm" c="#1f2328" mt="xs">
            Filters
          </Text>
          <Group gap="md" align="flex-end" wrap="wrap">
            <Select
              label="Vendor"
              placeholder={loadingVendors ? 'Loading…' : 'All vendors'}
              clearable
              searchable
              data={vendors}
              value={vendor}
              onChange={handleVendorChange}
              disabled={loadingVendors}
              w={220}
              styles={{ input: { borderColor: '#d0d7de' } }}
            />
            <Select
              label="API name"
              placeholder={
                vendorSelected ? 'All APIs' : 'Select a vendor first'
              }
              clearable
              searchable
              data={apiNameOptions}
              value={apiName}
              onChange={setApiName}
              disabled={!vendorSelected}
              w={280}
              styles={{ input: { borderColor: '#d0d7de' } }}
            />
            <Select
              label="Status code"
              placeholder={
                vendorSelected ? 'All status codes' : 'Select a vendor first'
              }
              clearable
              searchable
              data={STATUS_CODE_OPTIONS}
              value={statusCode}
              onChange={setStatusCode}
              disabled={!vendorSelected}
              w={180}
              styles={{ input: { borderColor: '#d0d7de' } }}
            />
            <Button
              onClick={fetchBreakdown}
              loading={loading}
              radius="sm"
              style={{ background: '#1563ff', fontWeight: 600 }}
            >
              Apply
            </Button>
          </Group>
        </Stack>
      </Paper>

      <Paper
        p="md"
        radius="sm"
        withBorder
        style={{ borderColor: '#d0d7de', background: '#ffffff' }}
      >
        <Group justify="space-between" mb="md">
          <Text fw={600} size="sm" c="#1f2328">
            API breakdown
          </Text>
          <Group gap="md">
            <Text size="sm" c="dimmed">
              Total {totals.total.toLocaleString()} · Success{' '}
              {totals.success.toLocaleString()} · Failure{' '}
              {totals.failure.toLocaleString()}
            </Text>
            <Text size="sm" c="dimmed">
              {items.length} API{items.length === 1 ? '' : 's'}
            </Text>
          </Group>
        </Group>

        {loading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : items.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No breakdown data for the selected filters.
          </Text>
        ) : (
          <Stack gap="md">
            <ScrollArea>
              <Table striped highlightOnHover withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>API name</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Total</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Success</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Failure</Table.Th>
                    <Table.Th>Status codes</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pageItems.map((row) => (
                    <Table.Tr key={row.api_name}>
                      <Table.Td>
                        <Text size="sm" style={{ wordBreak: 'break-all' }}>
                          {row.api_name}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        {row.total.toLocaleString()}
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm" c="green.7">
                          {row.success.toLocaleString()}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text
                          size="sm"
                          c={row.failure > 0 ? 'red.7' : 'dimmed'}
                          fw={row.failure > 0 ? 600 : 400}
                        >
                          {row.failure.toLocaleString()}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={6}>
                          {Object.entries(row.status_codes ?? {})
                            .sort(([a], [b]) => Number(a) - Number(b))
                            .map(([code, count]) => (
                              <Badge
                                key={code}
                                variant="light"
                                color={
                                  Number(code) >= 200 && Number(code) < 300
                                    ? 'green'
                                    : Number(code) >= 400
                                      ? 'red'
                                      : 'gray'
                                }
                                size="sm"
                              >
                                {code}: {count.toLocaleString()}
                              </Badge>
                            ))}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>

            {totalPages > 1 && (
              <Group justify="center">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={totalPages}
                  size="sm"
                />
              </Group>
            )}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};

export default BreakdownView;
